import { useEffect, useMemo, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { ArrowRight } from "lucide-react"
import { api } from "../../api/client"
import { useApp } from "../../state/AppState"
import { Button, Field, Input, Select } from "../../components/ui"
import { guessTimeZone, zoneLabel } from "../../lib/format"
import { AuthLayout, OAuthButtons } from "./AuthLayout"
import { CodeStep } from "./CodeStep"

/**
 * Sign-up.
 *
 * Country rather than timezone, because that is the question people can answer.
 * Countries with one zone resolve without a second question; the handful that
 * genuinely need it show their zones in population order, so the default is
 * usually already right. It is changeable in settings afterwards.
 */
export function RegisterPage() {
	const { config, adoptSession, isSignedIn } = useApp()
	const navigate = useNavigate()
	const [params] = useSearchParams()

	const invitedEmail = params.get("email") || ""
	const next = "/app"
	const invited = Boolean(params.get("invite"))

	const [form, setForm] = useState({
		name: "",
		email: invitedEmail,
		password: "",
		role: "",
		company: "",
		country: "",
		timezone: ""
	})
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState(null)
	const [challenge, setChallenge] = useState(null)

	const countries = config?.countries || []

	// Pre-select the country whose zones contain the browser's own guess. Only
	// a suggestion - the field stays editable.
	useEffect(() => {
		if (form.country || !countries.length) return
		const guess = guessTimeZone()
		const match = countries.find((country) => country.timezones.includes(guess))
		if (match) setForm((current) => ({ ...current, country: match.code, timezone: guess }))
	}, [countries, form.country])

	useEffect(() => {
		if (isSignedIn) navigate(next, { replace: true })
	}, [isSignedIn, navigate, next])

	const zones = useMemo(
		() => countries.find((country) => country.code === form.country)?.timezones || [],
		[countries, form.country]
	)

	function set(field, value) {
		setForm((current) => ({ ...current, [field]: value }))
	}

	function setCountry(code) {
		const entry = countries.find((country) => country.code === code)
		setForm((current) => ({
			...current,
			country: code,
			timezone: entry?.timezones?.[0] || ""
		}))
	}

	async function submit(event) {
		event.preventDefault()
		setBusy(true)
		setError(null)
		try {
			const started = await api.register({
				...form,
				email: form.email.trim(),
				name: form.name.trim()
			})
			setChallenge(started)
		} catch (cause) {
			setError(cause.message)
		} finally {
			setBusy(false)
		}
	}

	if (challenge) {
		return (
			<CodeStep
				challenge={challenge}
				email={form.email.trim()}
				onBack={() => setChallenge(null)}
				onVerified={async (session) => {
					await adoptSession(session.token, session.user)
					navigate(next, { replace: true })
				}}
			/>
		)
	}

	const minimum = config?.password_min_length ?? 10

	return (
		<AuthLayout
			wide
			title={invited ? "You have been invited" : "Create your account"}
			description={
				invited
					? "Create an account with this address and you will land straight on the issue you were invited to."
					: "One account, no organisation to join. You will see only the issues you are invited to."
			}
			footer={
				<>
					Already have an account?{" "}
					<Link
						to="/sign-in"
						className="font-medium text-brand-700 hover:underline"
					>
						Sign in
					</Link>
				</>
			}
		>
			<div>
				<form onSubmit={submit} className="space-y-3.5">
					<div className="grid gap-3.5 sm:grid-cols-2">
						<Field label="Full name">
							<Input
								value={form.name}
								onChange={(event) => set("name", event.target.value)}
								placeholder="Your name"
								required
								autoFocus
							/>
						</Field>
						<Field
							label="Work email"
							hint="Using a work email helps the people you already deal with find you."
						>
							<Input
								type="email"
								autoComplete="email"
								value={form.email}
								onChange={(event) => set("email", event.target.value)}
								placeholder="you@company.com"
								readOnly={Boolean(invitedEmail)}
								required
							/>
						</Field>
					</div>

					<Field
						label="Password"
						hint={`At least ${minimum} characters, with a mix of letters and numbers.`}
					>
						<Input
							type="password"
							autoComplete="new-password"
							value={form.password}
							onChange={(event) => set("password", event.target.value)}
							required
						/>
					</Field>

					<div className="grid gap-3.5 sm:grid-cols-2">
						<Field label="Role" hint="Shown beside your name in a thread.">
							<Input
								value={form.role}
								onChange={(event) => set("role", event.target.value)}
								placeholder="Your job title"
							/>
						</Field>
						<Field label="Company">
							<Input
								value={form.company}
								onChange={(event) => set("company", event.target.value)}
								placeholder="Where you work"
							/>
						</Field>
					</div>

					<div className="grid gap-3.5 sm:grid-cols-2">
						<Field label="Country">
							<Select
								value={form.country}
								onChange={(event) => setCountry(event.target.value)}
								required
							>
								<option value="">Select your country</option>
								{countries.map((country) => (
									<option key={country.code} value={country.code}>
										{country.name}
									</option>
								))}
							</Select>
						</Field>
						<Field
							label="Timezone"
							hint={
								zones.length > 1
									? "Your country has more than one."
									: "Set from your country."
							}
						>
							<Select
								value={form.timezone}
								onChange={(event) => set("timezone", event.target.value)}
								disabled={zones.length <= 1}
							>
								{zones.length === 0 && <option value="">Pick a country first</option>}
								{zones.map((zone) => (
									<option key={zone} value={zone}>
										{zoneLabel(zone)}
									</option>
								))}
							</Select>
						</Field>
					</div>

					{error && <p className="text-[0.8125rem] text-rose-600">{error}</p>}

					<Button
						type="submit"
						variant="primary"
						size="lg"
						className="w-full"
						iconRight={ArrowRight}
						busy={busy}
						disabled={busy || !form.email.trim() || !form.password || !form.country}
					>
						Create account
					</Button>
				</form>

				<OAuthButtons providers={config?.oauth_providers} next={next} disabled={busy} />
			</div>
		</AuthLayout>
	)
}
